<template>
  <Tooltip
    v-model:shown="isShown"
    :triggers="[]"
    class="ui-asset-info asset-node"
    :class="{ 'ui-asset-info--truncate': isTruncate }"
    :placement="tooltipPlacement"
    :distance="tooltipDistance"
    :delay="DELAY_TOOLTIP"
    handle-resize
  >
    <span
      ref="containerRef"
      v-bind="$attrs"
      class="ui-asset-info__container"
      :class="containerClasses"
      @click="handleClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <slot>{{ displayName }}</slot>
    </span>
    <template #popper>
      <component :is="tooltipContent" />
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import { computed, h, ref, useTemplateRef } from 'vue'
import { useResizeObserver } from '@vueuse/core'
import { Placement, Tooltip } from 'floating-vue'

import { ASSET_FIELD } from '@/const'

import { handleCatchedError } from '@/helpers/common'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useDocumentsStore } from '@/store/documents/index'
import { UISkeleton } from '@ui/skeletons'

const DELAY_TOOLTIP = 300

defineOptions({ inheritAttrs: false })

type Props = {
  name?: string
  id?: string
  onlyTicker?: boolean

  nameAsKey?: boolean

  tooltipPlacement?: Placement
  tooltipDistance?: number

  clickable?: boolean

  lineClamp?: number
}

const { name, id, tooltipPlacement = 'top', clickable, lineClamp, onlyTicker, nameAsKey } = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()
const documentsStore = useDocumentsStore()

const containerRef = useTemplateRef('containerRef')

const isValueEllipsis = ref(false)

const loadingThumbnail = ref(false)
const isShown = ref(false)

const imageSrc = ref<string>()

const assetName = computed(() => name || assetInstance.value?.field<string>(ASSET_FIELD.NAME).value)
const assetTicker = computed(() => assetInstance.value?.field<string>(ASSET_FIELD.TICKER).value)

const isTickerMode = computed(() => onlyTicker && (assetTicker.value !== assetName.value))

const tooltipContent = computed(() => {
  const result = []
  if (imageSrc.value) {
    result.push(h('img', {
      src: imageSrc.value,
      alt: tooltipName.value,
      class: ['ui-asset-info__image', { 'ui-asset-info__image--ellipsis': isValueEllipsis.value || isTickerMode.value }]
    }))
  } else if (loadingThumbnail.value) {
    result.push(h(UISkeleton, { class: 'ui-asset-info__image-skeleton' }))
  }
  if (isValueEllipsis.value || isTickerMode.value) {
    result.push(h('span', tooltipName.value))
  }
  return h('span', result)
})

const isTruncate = computed(() => lineClamp && lineClamp === 1)

const containerClasses = computed(() => ({
  'ui-asset-info__container--clickable': clickable,
  'ui-asset-info__container--hoverable': !!thumbnailId.value,
  [`ui-asset-info__container--line-clamp-${lineClamp}`]: lineClamp && lineClamp > 1,
  ['ui-asset-info__container--truncate']: isTruncate.value,
}))

const assetInstance = computed(() => id ? 
  assetsBunchStore.getElementById(id) : 
  (nameAsKey ? assetsBunchStore.getElementByName(name!) : undefined)
)

const displayName = computed(() => onlyTicker ? assetTicker.value : assetName.value)
const tooltipName = computed(() => onlyTicker ? assetName.value : displayName.value)
const thumbnailId = computed(() => assetInstance.value?.thumbnailId)

const handleClick = (e: MouseEvent) => {
  if (!clickable || !assetInstance.value) return
  e.preventDefault()
  e.stopPropagation()
  assetInstance.value?.openDrawer()
}

const handleMouseEnter = async () => {
  if (isValueEllipsis.value || isTickerMode.value) {
    isShown.value = true
  }
  await getThumbnail()
  if (imageSrc.value) {
    isShown.value = true
  }
}

const handleMouseLeave = () => {
  isShown.value = false
}

const getThumbnail = async () => {
  if (!thumbnailId.value) {
    imageSrc.value = undefined
    return
  }
  if (assetInstance.value?.thumbnailUrl) {
    imageSrc.value = assetInstance.value.thumbnailUrl
    return
  }
  try {
    loadingThumbnail.value = true
    const document = await documentsStore.fetchSingle(thumbnailId.value)
    const thumbnail = document.url
    if (!thumbnail) return
     
    assetInstance.value && (assetInstance.value.thumbnailUrl = thumbnail)
    imageSrc.value = thumbnail
  } catch (e) {
    handleCatchedError(e as string)
  } finally {
    loadingThumbnail.value = false
  }
}

useResizeObserver(containerRef, () => {
  if (!containerRef.value) return
  isValueEllipsis.value =
    containerRef.value.offsetWidth < containerRef.value.scrollWidth ||
    containerRef.value.offsetHeight < containerRef.value.scrollHeight
})
</script>

<style>
.ui-asset-info {
  @apply w-max max-w-full;
  @apply whitespace-normal;

  &--truncate {
    @apply w-full;
    @apply max-w-none;
  }

  &__container {
    @apply w-full;
    @apply underline-offset-2;

    &--hoverable {
      @apply hover:underline decoration-dashed;
      @apply decoration-gray-200 dark:decoration-gray-700;
    }

    &--clickable {
      @apply underline hover:no-underline decoration-solid;
      @apply decoration-gray-300 dark:decoration-gray-500;
      @apply cursor-pointer;
    }

    &--line-clamp-2 {
      @apply line-clamp-2;
    }

    &--line-clamp-3 {
      @apply line-clamp-3;
    }

    &--line-clamp-4 {
      @apply line-clamp-4;
    }

    &--truncate {
      @apply block;
      @apply truncate;
    }
  }

  &__image,
  &__image-skeleton {
    @apply -mx-2.5 -my-1.5;
    @apply aspect-video object-cover;
  }

  &__image {
    @apply max-w-60;
    @apply rounded-sm;

    &--ellipsis {
      @apply mb-1.5;
      @apply rounded-b-none;
    }
  }

  &__image-skeleton {
    @apply w-60;
    @apply bg-gray-200;
    @apply animate-pulse;
  }
}
</style>
